import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AcademicYear } from '@/models';
import { queryKeys } from '@/lib/queryKeys';
import { useAcademicYears } from './academics';
import { useCurrentSchool } from './useCurrentSchool';
import { useActiveAcademicYear, useGlobalSelector } from './useGlobalState';

/** Why there is no working year: the school marks none as current, or its current year has ended. */
export type AcademicYearMissingReason = 'none' | 'ended';

/** The working academic year, its status and the school's years. */
export interface CurrentAcademicYear {
  years: AcademicYear[];
  /**
   * The years are loading, or the working year is being set from them. Pages must wait:
   * nothing should query with a year until this is false.
   */
  isLoading: boolean;
  /** The years could not be loaded. */
  error: unknown;
  refetch: () => void;
  /** The school's current year (`isCurrent`), or null when none is marked. */
  schoolYearId: number | null;
  /** The working year held in the global state, or `''` when there is none. */
  chosenYearId: number | '';
  /**
   * Sets the working year. Any of the school's years can be chosen, a past (closed or
   * ended) one included, to view it; a year that is not one of the school's years is
   * refused. `''` goes back to the school's current year.
   */
  setChosenYearId: (yearId: number | '') => void;
  /** The working year pages should use — from the global state only, never guessed. */
  currentYearId: number | null;
  currentYear: AcademicYear | null;
  /** The working year is the school's current year. */
  isDefault: boolean;
  /** The years loaded and there is no working year. */
  missing: boolean;
  /** Why the working year is missing (meaningful only when `missing`). */
  missingReason: AcademicYearMissingReason;
  /** The ended current year's name when `missingReason` is `'ended'`, else null. */
  endedYearName: string | null;
}

/** Today as `YYYY-MM-DD` in local time, the format year dates use. */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A past year: Closed, or ended before today. It is never picked as the working year by
 * default, but the user may choose it to view that year.
 */
export function isPastAcademicYear(year: AcademicYear, today: string = todayIso()): boolean {
  return year.status === 'Closed' || year.yearEndDate.slice(0, 10) < today;
}

/** Why there is no working year, in words a user can act on. */
export function missingAcademicYearMessage({
  missingReason,
  endedYearName,
}: Pick<CurrentAcademicYear, 'missingReason' | 'endedYearName'>): string {
  return missingReason === 'ended'
    ? `The current academic year ${endedYearName ?? ''} has ended. Ask an administrator to mark the new year as current.`
    : 'No current academic year is set for this school. Ask an administrator to mark one as current in Academic Years.';
}

/** The school's current year when it can be the working year, else null. */
function usableCurrentYearId(years: readonly AcademicYear[], today: string): number | null {
  const current = years.find((y) => y.isCurrent);
  return current && !isPastAcademicYear(current, today) ? current.yearId : null;
}

/**
 * The academic year the app is working in, shared across pages. It lives in the
 * global state (`activeAcademicYearId`). On app start and every school switch it
 * is set from the school's current year; a year restored from the session is kept
 * when it is still usable.
 *
 * Once set, the working year is only replaced when it disappears from the
 * school's years (deleted) or the user picks another one — not because it was
 * closed or ended mid-session, so open forms are never wiped by a background
 * refetch. A past (closed or ended) working year is view only (see
 * `useAcademicYearWritable`) and the server refuses writes to it, except for
 * closed-year administrators.
 *
 * No usable current year → `missing` (with `missingReason`), and pages show an
 * error instead of guessing.
 *
 * Every caller keeps the state in step (the dispatch is idempotent), so a page
 * works the same inside `AppShell` and on its own.
 */
export function useCurrentAcademicYear(): CurrentAcademicYear {
  const { data, isLoading: yearsLoading, error, refetch } = useAcademicYears();
  const { currentSchoolId } = useCurrentSchool();
  const queryClient = useQueryClient();
  const [storedYearId, setStoredYearId] = useActiveAcademicYear();
  const confirmed = useGlobalSelector((s) => s.activeAcademicYearConfirmed);
  const years = data ?? [];
  const loaded = data !== undefined;

  const today = todayIso();
  const current = years.find((y) => y.isCurrent) ?? null;
  const stored = years.find((y) => y.yearId === storedYearId) ?? null;
  // A confirmed year stays while it is in the list; a restored one must still be usable.
  const keepStored = stored !== null && (confirmed || !isPastAcademicYear(stored, today));
  // What the state should hold once the years are known.
  const targetYearId = keepStored ? stored.yearId : usableCurrentYearId(years, today);
  const storedOrNull = storedYearId === '' ? null : storedYearId;
  const pending = loaded && targetYearId !== storedOrNull;
  // A restored year that is still usable only needs marking as checked; its id does not change.
  const unconfirmed = loaded && !pending && storedOrNull !== null && !confirmed;

  useEffect(() => {
    if (pending || unconfirmed) setStoredYearId(targetYearId ?? '');
  }, [pending, unconfirmed, targetYearId, setStoredYearId]);

  // Checked against the freshest cached list, so a caller can set a year straight after a
  // mutation has refetched the years, before its own render has caught up.
  const setChosenYearId = useCallback(
    (yearId: number | '') => {
      const list = queryClient.getQueryData<AcademicYear[]>(queryKeys.academics.yearsFor(currentSchoolId)) ?? [];
      const now = todayIso();
      if (yearId === '') {
        setStoredYearId(usableCurrentYearId(list, now) ?? '');
        return;
      }
      const year = list.find((y) => y.yearId === yearId);
      if (!year) return;
      setStoredYearId(yearId);
    },
    [queryClient, currentSchoolId, setStoredYearId],
  );

  const workingYear = keepStored ? stored : null;
  const workingYearId = workingYear?.yearId ?? null;
  const missing = loaded && !pending && workingYearId === null;
  const ended = current !== null && isPastAcademicYear(current, today);
  return {
    years,
    isLoading: yearsLoading || pending,
    error,
    refetch: () => void refetch(),
    schoolYearId: current?.yearId ?? null,
    chosenYearId: workingYearId ?? '',
    setChosenYearId,
    currentYearId: workingYearId,
    currentYear: workingYear,
    isDefault: workingYearId !== null && workingYearId === current?.yearId,
    missing,
    missingReason: ended ? 'ended' : 'none',
    endedYearName: ended ? current.yearName : null,
  };
}
